import { type FC, type FormEvent, useState } from "react";

import type { DashboardSourceControlRepositoryUpsertRequest } from "../../dashboard-client/types.gen";

import {
  type DiscoveredRepository,
  discoverSourceControlRepositories,
} from "../../api/sourceControl";
import { Button } from "../common/Button";
import { FormCheckbox, FormInput, FormMessage } from "../common/forms";
import { SearchableOptionList } from "../common/SearchableOptionList";
import { Text } from "../common/Text";

interface SourceControlRepositoryFormProps {
  providerId: string;
  onUpsert: (
    providerId: string,
    input: DashboardSourceControlRepositoryUpsertRequest,
  ) => Promise<void>;
}

const defaultRepoForm: DashboardSourceControlRepositoryUpsertRequest = {
  default_branch: "",
  is_required: false,
  remote_slug: "",
  repository_id: "",
};

/**
 * Form for adding a new repository to a source control provider.
 * Includes discovery feature to load repositories from the provider.
 */
export const SourceControlRepositoryForm: FC<
  SourceControlRepositoryFormProps
> = ({ providerId, onUpsert }) => {
  const [form, setForm] = useState(defaultRepoForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [discoveredRepos, setDiscoveredRepos] = useState<
    DiscoveredRepository[]
  >([]);
  const [discovering, setDiscovering] = useState(false);

  const handleDiscover = async () => {
    try {
      setDiscovering(true);
      setError(null);
      setSuccessMessage(null);
      const repos = await discoverSourceControlRepositories(providerId);
      setDiscoveredRepos(repos);
      if (repos.length === 0) {
        setSuccessMessage(
          "No repositories found. If the GitHub App was installed with limited repository access, reinstall it and grant the repositories you want Arti to see.",
        );
      }
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : String(caughtError);
      setError(message);
    } finally {
      setDiscovering(false);
    }
  };

  const handleSelectRepo = (repositoryId: string) => {
    const repo = discoveredRepos.find(
      (candidate) => candidate.id === repositoryId,
    );
    if (repo) {
      // Use providerId:remoteId format to match auto-indexer
      const id = `${providerId}:${repo.id}`;
      setForm((prev) => ({
        ...prev,
        remote_slug: repo.name,
        repository_id: id,
      }));
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.repository_id.trim() || !form.remote_slug.trim()) {
      setError("Repository ID and remote slug are required.");
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);
      await onUpsert(providerId, form);
      setForm(defaultRepoForm);
      setSuccessMessage("Repository added successfully.");
    } catch (caughtError) {
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : String(caughtError);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-5 border-t border-gray-200 pt-4 dark:border-white/10">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
          Add Repository
        </h4>
        <Button
          type="button"
          onClick={() => void handleDiscover()}
          disabled={discovering}
          variant="outline"
          size="sm"
          className="px-2 py-1 disabled:opacity-50"
        >
          {discovering ? "Loading..." : "Load from Provider"}
        </Button>
      </div>

      {error && (
        <div className="mb-3">
          <FormMessage type="error">{error}</FormMessage>
        </div>
      )}
      {successMessage && (
        <div className="mb-3">
          <FormMessage type="success">{successMessage}</FormMessage>
        </div>
      )}

      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="space-y-3"
      >
        {discoveredRepos.length > 0 && (
          <div className="mb-3 space-y-2">
            <SearchableOptionList
              label="Select from Provider"
              selectedValue={
                discoveredRepos.find(
                  (repo) => `${providerId}:${repo.id}` === form.repository_id,
                )?.id ?? null
              }
              onSelect={handleSelectRepo}
              searchPlaceholder="Search repositories..."
              emptyMessage="No repositories match your search."
              items={discoveredRepos.map((repo) => ({
                description:
                  repo.projectName && repo.projectName !== repo.name
                    ? repo.projectName
                    : repo.url,
                keywords: [repo.name, repo.projectName ?? "", repo.url],
                label: repo.name,
                meta: repo.id,
                value: repo.id,
              }))}
            />
            <Text className="leading-relaxed text-slate-500 dark:text-slate-400">
              Only repositories granted to the GitHub App installation appear
              here. If an expected repository is missing, reinstall the app and
              widen repository access.
            </Text>
          </div>
        )}

        <FormInput
          label="Repository ID"
          placeholder="e.g. arti-github:123456789"
          value={form.repository_id}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, repository_id: event.target.value }))
          }
          required
        />

        <FormInput
          label="Remote Slug"
          placeholder="e.g. VaultN/agent-arti"
          value={form.remote_slug}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, remote_slug: event.target.value }))
          }
          required
        />

        <FormInput
          label="Default Branch (Optional)"
          placeholder="e.g. main"
          value={form.default_branch ?? ""}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, default_branch: event.target.value }))
          }
        />
        <Text className="leading-relaxed text-slate-500 dark:text-slate-400">
          Leave this blank to let Arti resolve the repository&apos;s default
          branch from GitHub when possible.
        </Text>

        <FormCheckbox
          label="Required for automation"
          checked={form.is_required ?? false}
          onChange={(checked) =>
            setForm((prev) => ({ ...prev, is_required: checked }))
          }
        />

        <Button
          type="submit"
          loading={submitting}
          loadingText="Saving…"
          fullWidth
        >
          Save Repository
        </Button>
      </form>
    </div>
  );
};
